import { call, put, takeEvery, select } from 'redux-saga/effects';
import { getFeatures } from '../requests/FeaturesRequests';
import { loadFeaturesSuccess } from '../actions/FeaturesActions';
import { FeaturesCache } from '../utils/FeaturesCache';

function* loadFeaturesSaga() {
    try {
        const appStore = yield select(state => state.appStore);
        const isLoggedIn = appStore.session_id !== null;

        if (!isLoggedIn) {
            yield put(loadFeaturesSuccess({}));
            return;
        }

        const cachedFeatures = FeaturesCache.load();
        if (cachedFeatures !== null) {
            yield put(loadFeaturesSuccess(cachedFeatures));
        }

        // Cache miss or expired, fetch from API
        const response = yield call(getFeatures);

        // Handle different possible response structures
        const features = response.features || response || {};

        if (JSON.stringify(cachedFeatures) !== JSON.stringify(features)) {
            FeaturesCache.save(features);
            yield put(loadFeaturesSuccess(features));
        }
    } catch (error) {
        console.error('Failed to load features:', error);

        // Try to use expired cache as last resort
        try {
            const cachedData = localStorage.getItem('minute7_features_cache');
            if (cachedData) {
                const { features } = JSON.parse(cachedData);
                yield put(loadFeaturesSuccess(features || {}));
                return;
            }
        } catch (cacheError) {
            console.warn('Failed to load expired cache:', cacheError);
        }

        // Don't fail the app if features can't be loaded, use defaults and mark as loaded
        // This ensures the app still renders even if the features API is down
        yield put(loadFeaturesSuccess({})); // Empty features object as fallback
    }
}

function* refreshFeaturesSaga() {
    try {
        const appStore = yield select(state => state.appStore);
        const isLoggedIn = appStore.session_id !== null;

        if (!isLoggedIn) {
            yield put(loadFeaturesSuccess({})); // Return empty features for non-authenticated users
            return;
        }

        // Clear cache first to force fresh fetch
        FeaturesCache.clear();

        // Fetch from API
        console.log('Refreshing features from API');
        const response = yield call(getFeatures);

        // Handle different possible response structures
        const features = response.features || response || {};

        // Cache the features
        FeaturesCache.save(features);

        yield put(loadFeaturesSuccess(features));
    } catch (error) {
        console.error('Failed to refresh features:', error);
        yield put(loadFeaturesSuccess({})); // Empty features object as fallback
    }
}

function* clearFeaturesCacheSaga() {
    try {
        yield FeaturesCache.clear();
        console.log('Features cache cleared via action');
    } catch (error) {
        console.error('Failed to clear features cache:', error);
    }
}

function* featuresSaga() {
    yield takeEvery('LOAD_FEATURES_REQUEST', loadFeaturesSaga);
    yield takeEvery('REFRESH_FEATURES_REQUEST', refreshFeaturesSaga);
    yield takeEvery('CLEAR_FEATURES_CACHE', clearFeaturesCacheSaga);
}

export default featuresSaga;
